import {
  ADDRESS_BOOK_FETCH_STARTED,
  ADDRESS_BOOK_FETCH_SUCCEEDED,
  ADDRESS_BOOK_FETCH_FAILED,
  ADDRESS_BOOK_CLEAR,
  SUCCESS
} from '#action/types';
import { webPortalApi } from '#config/web-portal-api';
import { snackBarError } from '#action/fetch';
import { fetchProject } from '#action/project/dashboard';
import { SubmissionError } from '#helper/submission-error';
import { history } from '#configureStore';
import { getRequestOptions } from '#helper';

let abortController = new AbortController();

export const fetchAddressbook = (id, withProject) => async dispatch => {
  try {
    abortController.abort();
    const addressbookUrl = webPortalApi.buildApiUrl(`addressbook/${id}`);
    dispatch({
      type: ADDRESS_BOOK_FETCH_STARTED
    });
    abortController = new AbortController();
    const { signal } = abortController;
    const res = await fetch(addressbookUrl, { ...getRequestOptions(), signal });
    const contentType = res.headers.get('content-type');

    if (!contentType || !contentType.includes('application/json')) {
      throw new TypeError('Response is not json');
    }

    const addressbook = await res.json();

    if (!res.ok) {
      const shallRedirect = [403, 404].includes(addressbook.resStatus);

      dispatch(
        snackBarError({
          message: 'Error in request',
          ...addressbook
        })
      );

      if (shallRedirect) {
        await history.push('/addressbook');
      }

      throw addressbook;
    }

    if (withProject && addressbook.project?.id) {
      const project = await fetchProject(addressbook.project?.id);

      dispatch({
        type: ADDRESS_BOOK_FETCH_SUCCEEDED,
        payload: {
          ...addressbook,
          project
        }
      });

      return;
    }

    dispatch({
      type: ADDRESS_BOOK_FETCH_SUCCEEDED,
      payload: addressbook
    });
  } catch (e) {
    if (e.name === 'AbortError') {
      return;
    }
    dispatch({
      type: ADDRESS_BOOK_FETCH_FAILED,
      error: e
    });
  }
};

export const clear = () => dispatch => {
  abortController.abort();
  dispatch({
    type: ADDRESS_BOOK_CLEAR
  });
};

export const createAddressbook = addressbook => async dispatch => {
  try {
    const addressbookUrl = webPortalApi.buildApiUrl('addressbook');
    const res = await fetch(addressbookUrl, {
      method: 'POST',
      body: JSON.stringify(addressbook),
      ...getRequestOptions()
    });
    const contentType = res.headers.get('content-type');

    if (!contentType || !contentType.includes('application/json')) {
      throw new TypeError('Response is not json');
    }
    if (!res.ok) {
      throw await res.json();
    }

    dispatch({
      type: SUCCESS,
      payload: {
        message: 'Address book has been created'
      }
    });

    return await res.json();
  } catch (e) {
    dispatch(snackBarError({
      message: 'Error in request',
      ...e
    }));
    throw new SubmissionError(e);
  }
};

export const updateAddressbook = (addressbookId, addressbook) => async dispatch => {
  try {
    const addressbookUrl = webPortalApi.buildApiUrl(`addressbook/${addressbookId}`);
    const res = await fetch(addressbookUrl, {
      method: 'PATCH',
      body: JSON.stringify(addressbook),
      ...getRequestOptions()
    });
    const contentType = res.headers.get('content-type');

    if (!contentType || !contentType.includes('application/json')) {
      throw new TypeError('Response is not json');
    }
    if (!res.ok) {
      throw await res.json();
    }

    dispatch({
      type: SUCCESS,
      payload: {
        message: 'Address book has been updated'
      }
    });

    return await res.json();
  } catch (e) {
    dispatch(snackBarError({
      message: 'Error in request',
      ...e
    }));
    throw new SubmissionError(e);
  }
};

export const deleteAddressbook = addressbookId => async dispatch => {
  try {
    const addressbookUrl = webPortalApi.buildApiUrl(`addressbook/${addressbookId}`);
    const res = await fetch(addressbookUrl, {
      method: 'DELETE',
      ...getRequestOptions()
    });
    const contentType = res.headers.get('content-type');

    if (!contentType || !contentType.includes('application/json')) {
      throw new TypeError('Response is not json');
    }
    if (!res.ok) {
      throw await res.json();
    }

    dispatch({
      type: SUCCESS,
      payload: {
        message: 'Address book has been deleted'
      }
    });

    return await res.json();
  } catch (e) {
    dispatch(snackBarError({
      message: 'Error in request',
      ...e
    }));
    return null;
  }
};
